import React, { useEffect, useState } from 'react';
import { collection, getDocs, doc, deleteDoc, updateDoc } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { db, storage } from '../../services/firebase';
import { useAuth } from '../../context/AuthContext';
import { Artwork } from '../../types';
import { Trash2, Eye, Star, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const ManageArtworks: React.FC = () => {
  const { user } = useAuth();
  const [artworks, setArtworks] = useState<Artwork[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);

  useEffect(() => {
    loadArtworks();
  }, []);

  const loadArtworks = async () => {
    setLoading(true);
    setError(null);
    try {
      const querySnapshot = await getDocs(collection(db, 'artworks'));
      const artworksData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
      })) as Artwork[];
      setArtworks(artworksData);
    } catch (error: any) {
      console.error('Error loading artworks:', error);
      setError('Failed to load artworks. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePromote = async (artworkId: string, currentStatus: boolean) => {
    try {
      await updateDoc(doc(db, 'artworks', artworkId), {
        isPromoted: !currentStatus,
      });
      await loadArtworks();
    } catch (error: any) {
      console.error('Error updating artwork:', error);
      alert('Failed to update promotion status. Please check your permissions.');
    }
  };

  const handleDeleteArtwork = async (artwork: Artwork) => {
    if (!window.confirm(`Are you sure you want to delete "${artwork.title}"? This action cannot be undone.`)) {
      return;
    }
    
    setDeleteLoading(artwork.id);
    setError(null);

    try {
      // Delete the artwork document from Firestore
      await deleteDoc(doc(db, 'artworks', artwork.id));

      // Try to delete the image from Storage (optional - may fail if storage rules are different)
      try {
        if (artwork.imageUrl) {
          const imageRef = ref(storage, artwork.imageUrl);
          await deleteObject(imageRef);
        }
      } catch (storageError) {
        console.warn('Could not delete image from storage:', storageError);
        // Continue even if storage deletion fails
      }

      // Reload artworks
      await loadArtworks();
      
      // Show success message
      alert('Artwork deleted successfully!');
    } catch (error: any) {
      console.error('Error deleting artwork:', error);
      
      // Provide more specific error messages
      if (error.code === 'permission-denied') {
        setError('Permission denied. You can only delete your own artworks or you need admin access.');
      } else if (error.code === 'not-found') {
        setError('Artwork not found. It may have been already deleted.');
      } else {
        setError(`Failed to delete artwork: ${error.message}`);
      }
      
      alert(error.code === 'permission-denied' 
        ? 'You do not have permission to delete this artwork. Only the artist or admin can delete it.'
        : 'Failed to delete artwork. Please try again.');
    } finally {
      setDeleteLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading artworks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Manage Artworks</h1>
        <p className="text-gray-600">Total artworks: {artworks.length}</p>
      </div>

      {/* Error Alert */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3"
          >
            <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
            <div className="flex-1">
              <p className="text-red-800 font-medium">Error</p>
              <p className="text-red-600 text-sm">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-500 hover:text-red-700"
            >
              ✕
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {artworks.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl shadow-sm">
          <div className="text-6xl mb-4">🎨</div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">No artworks found</h3>
          <p className="text-gray-600">Artworks will appear here once users start posting.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {artworks.map((artwork, index) => (
            <motion.div
              key={artwork.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all"
            >
              <div className="relative">
                <img
                  src={artwork.imageUrl}
                  alt={artwork.title}
                  className="w-full h-48 object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x300?text=Image+Not+Found';
                  }}
                />
                {artwork.isPromoted && (
                  <span className="absolute top-2 left-2 px-3 py-1 bg-yellow-400 text-primary rounded-full text-xs font-bold">
                    PROMOTED
                  </span>
                )}
              </div>
              
              <div className="p-4">
                <h3 className="font-bold text-gray-800 mb-1 truncate" title={artwork.title}>
                  {artwork.title}
                </h3>
                <p className="text-sm text-gray-600 mb-2">by {artwork.artistName}</p>
                <div className="flex items-center gap-3 text-sm text-gray-500 mb-3">
                  <span className="flex items-center gap-1">
                    <Eye size={16} />
                    {artwork.views || 0}
                  </span>
                  <span>₹{artwork.price.toLocaleString()}</span>
                </div>
                
                <div className="flex gap-2">
                  <button
                    onClick={() => handleTogglePromote(artwork.id, artwork.isPromoted)}
                    disabled={deleteLoading === artwork.id}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      artwork.isPromoted
                        ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                        : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                    }`}
                  >
                    <Star size={16} className="inline mr-1" />
                    {artwork.isPromoted ? 'Promoted' : 'Promote'}
                  </button>
                  <button
                    onClick={() => handleDeleteArtwork(artwork)}
                    disabled={deleteLoading === artwork.id}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed relative"
                    title="Delete artwork"
                  >
                    {deleteLoading === artwork.id ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600"></div>
                    ) : (
                      <Trash2 size={18} />
                    )}
                  </button>
                </div>
                
                {/* Artist info for admin */}
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-xs text-gray-500">
                    Artist ID: <span className="font-mono">{artwork.artistId.slice(0, 8)}...</span>
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ManageArtworks;